import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useTrialStatus } from '@/hooks/useTrialStatus';

/**
 * Always-visible trial state in the app header, with a one-click path to
 * subscribe.
 *
 * Before this, the ONLY place the trial existed in the UI was the
 * Subscription page — nested under Administration, owner-only, and with
 * no reason for a trialing clinic to ever open it. A clinic could run the
 * full 30 days, drop to FREE, lose AI, and never once have seen the word
 * "trial" or a way to pay. This is the standing reminder + CTA.
 *
 * Non-owners see the state but no CTA: they can't buy, so sending them to
 * a billing page is a dead end.
 */
export const TrialPill: React.FC = () => {
  const { isRTL } = useTranslation();
  const { phase, daysLeft, canSubscribe, show } = useTrialStatus();

  if (!show) return null;

  const lapsed = phase === 'lapsed';
  const ending = phase === 'ending';
  // Neutral while there's time; warning once it's close or over — the
  // colour is the urgency signal, so it must not cry wolf on day 1.
  const tone = lapsed
    ? 'var(--color-error)'
    : ending
      ? 'var(--color-warning)'
      : 'var(--color-primary-600)';

  // Two lengths. The header already carries five controls on a phone, and
  // a full "25d left · Subscribe" pill crowded them into the notch — so
  // the phone gets the number alone (still the whole point of the pill)
  // and the wording returns at sm.
  const shortLabel = lapsed
    ? isRTL
      ? 'انتهت'
      : 'Ended'
    : daysLeft === 0
      ? isRTL
        ? '<١ ي'
        : '<1d'
      : isRTL
        ? `${daysLeft} ي`
        : `${daysLeft}d`;

  const label = lapsed
    ? isRTL
      ? 'انتهت التجربة'
      : 'Trial ended'
    : daysLeft === 0
      ? isRTL
        ? 'آخر يوم'
        : 'Last day'
      : isRTL
        ? `${daysLeft} يوم متبقٍ`
        : `${daysLeft}d left`;

  const title = lapsed
    ? isRTL
      ? 'انتهت فترة التجربة — ميزات الذكاء الاصطناعي متوقفة حتى الاشتراك'
      : 'Your trial has ended — AI features are paused until you subscribe'
    : isRTL
      ? 'أنت في فترة تجربة مجانية'
      : "You're on a free trial";

  const content = (
    <>
      {lapsed ? (
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
      ) : (
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
      )}
      <span className="whitespace-nowrap sm:hidden">{shortLabel}</span>
      <span className="whitespace-nowrap hidden sm:inline">{label}</span>
      {canSubscribe && (
        <span
          className="hidden sm:inline whitespace-nowrap font-semibold underline underline-offset-2"
          // Spelling out the action is the whole point — a bare countdown
          // states a problem without offering the fix.
        >
          {isRTL ? 'اشترك' : 'Subscribe'}
        </span>
      )}
    </>
  );

  const className =
    'inline-flex items-center gap-1.5 px-2.5 min-h-[36px] rounded-full text-xs font-medium border transition-colors';
  const style: React.CSSProperties = {
    color: tone,
    borderColor: `color-mix(in srgb, ${tone} 40%, transparent)`,
    backgroundColor: `color-mix(in srgb, ${tone} 10%, transparent)`,
  };

  if (!canSubscribe) {
    return (
      <span className={className} style={style} title={title}>
        {content}
      </span>
    );
  }

  return (
    <Link
      to="/admin/subscription"
      className={className}
      style={style}
      title={title}
      aria-label={title}
    >
      {content}
    </Link>
  );
};

export default TrialPill;
